import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { scrollToSection } from "@/App";

const links = [
  { label: "Services", hash: "#services", testid: "nav-link-services" },
  { label: "Why Us", hash: "#why-us", testid: "nav-link-why-us" },
  { label: "Clients", hash: "#testimonials", testid: "nav-link-testimonials" },
  { label: "Contact", hash: "#contact", testid: "nav-link-contact" },
];

const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-[80] border-b border-white/10 bg-[#0A0F1C]/70 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <a
          href="#top"
          onClick={scrollToSection("#top")}
          data-testid="logo-link"
          className="font-display text-lg font-black tracking-tight"
        >
          FUTURE<span className="text-sky-400">/</span>
          <span className="ml-1 font-medium text-slate-400">SOLUTIONS</span>
        </a>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l.hash}
              href={l.hash}
              onClick={scrollToSection(l.hash)}
              data-testid={l.testid}
              className="text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-sky-400"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            onClick={scrollToSection("#contact")}
            data-testid="header-cta-button"
            className="group hidden items-center gap-2 rounded-full bg-sky-400 px-5 py-2.5 text-sm font-bold text-[#0A0F1C] transition-colors duration-300 hover:bg-white md:inline-flex"
          >
            Get a Quote
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="text-white md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#0A0F1C]/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <a
                  key={l.hash}
                  href={l.hash}
                  data-testid={`mobile-${l.testid}`}
                  onClick={(e) => {
                    setOpen(false);
                    scrollToSection(l.hash)(e);
                  }}
                  className="py-3 font-display text-2xl font-bold text-white transition-colors duration-300 hover:text-sky-400"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                data-testid="mobile-header-cta"
                onClick={(e) => {
                  setOpen(false);
                  scrollToSection("#contact")(e);
                }}
                className="mt-3 inline-flex w-fit items-center gap-2 rounded-full bg-sky-400 px-6 py-3 text-sm font-bold text-[#0A0F1C]"
              >
                Get a Quote <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
